import { getLocale } from "../../i18n";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import "../../utils/chartjs";
import { Bar } from 'react-chartjs-2';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';
import '../../assets/css/insights.css';

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtHours(h) {
  if (!h || h === 0) return '0h';
  const hrs = Math.floor(h);
  const min = Math.round((h - hrs) * 60);
  return min > 0 ? `${hrs}h ${min}m` : `${hrs}h`;
}

function deltaLabel(thisW, lastW) {
  if (lastW === 0) return null;
  const pct = Math.round(((thisW - lastW) / lastW) * 100);
  if (pct === 0) return { get text() { return tr("Same as last week"); }, cls: 'flat' };
  return { text: `${pct > 0 ? '+' : ''}${pct}% vs last week`, cls: pct > 0 ? 'up' : 'down' };
}

function shortDate(isoDate) {
  return new Date(isoDate + 'T00:00:00').toLocaleDateString(getLocale(), { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtUpcomingDate(isoDate) {
  return new Date(isoDate + 'T00:00:00').toLocaleDateString(getLocale(), { month: 'short', day: 'numeric' });
}

// ── Stat card ──────────────────────────────────────────────────────────────

function StatCard({ label, value, unit = '', delta, children, accent }) {
  useTranslation();
  return (
    <div className="ins-card" style={accent ? { borderTop: `3px solid ${accent}` } : {}}>
      <div className="ins-card-label">{label}</div>
      {value !== undefined && (
        <div className="ins-card-value">{value}<span>{unit}</span></div>
      )}
      {delta && <div className={`ins-card-delta ins-card-delta--${delta.cls}`}>{delta.text}</div>}
      {children}
    </div>
  );
}

// ── Leave balance card ─────────────────────────────────────────────────────

function LeaveCard({ leaveRemaining }) {
  useTranslation();
  return (
    <div className="ins-card">
      <div className="ins-card-label">{tr("Leave Remaining")}</div>
      {leaveRemaining.length === 0 ? (
        <p className="ins-leave-empty">{tr("No balances set")}</p>
      ) : (
        <div className="ins-leave-pills">
          {leaveRemaining.map((l, i) => (
            <div className="ins-leave-row" key={i}>
              <div className="ins-leave-name">
                <span className="ins-leave-dot" style={{ background: l.color }} />
                {l.name}
              </div>
              <div className="ins-leave-days">{l.remaining}{tr("d")}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Daily bar chart ────────────────────────────────────────────────────────

function DailyBarChart({ daily7d }) {
  useTranslation();
  const data = {
    labels  : daily7d.map((d) => shortDate(d.date)),
    datasets: [{
      get label() { return tr("Hours"); },
      data           : daily7d.map((d) => d.hours),
      backgroundColor: '#1a5c30cc',
      borderRadius   : 6,
      borderSkipped  : false,
    }],
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y}h` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#718174', font: { size: 11 } } },
      y: { beginAtZero: true, ticks: { color: '#718174', font: { size: 11 }, callback: (v) => `${v}h` }, grid: { color: '#edf3ee' } },
    },
  };
  return (
    <div className="ins-chart-box">
      <p className="ins-chart-title">{tr("Hours tracked — last 7 days")}</p>
      <div className="ins-chart-wrap"><Bar key={getLocale()} data={data} options={options} /></div>
    </div>
  );
}

// ── Day-of-week average bar chart ──────────────────────────────────────────

function DowChart({ byDow }) {
  useTranslation();
  // Mon–Fri only, reorder from Sun-first to Mon-first
  const ordered = [...byDow.slice(1), byDow[0]]; // Mon Tue Wed Thu Fri Sat Sun
  const data = {
    labels  : ordered.map((d) => d.label),
    datasets: [{
      get label() { return tr("Avg hours"); },
      data           : ordered.map((d) => d.avg),
      backgroundColor: ordered.map((d, i) =>
        i < 5 ? '#4bc800b3' : '#e6a20a99'
      ),
      borderRadius: 6,
      borderSkipped: false,
    }],
  };
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.parsed.y}h avg` } },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#718174', font: { size: 11 } } },
      y: { beginAtZero: true, ticks: { color: '#718174', font: { size: 11 }, callback: (v) => `${v}h` }, grid: { color: '#edf3ee' } },
    },
  };
  return (
    <div className="ins-chart-box">
      <p className="ins-chart-title">{tr("Avg hours by day of week — this month")}</p>
      <div className="ins-chart-wrap"><Bar key={getLocale()} data={data} options={options} /></div>
    </div>
  );
}

// ── Project pie chart ──────────────────────────────────────────────────────

function ProjectBreakdown({ byProject }) {
  useTranslation();
  if (byProject.length === 0) {
    return (
      <div className="ins-project-breakdown">
        <p className="ins-chart-title">{tr("Project focus — this month")}</p>
        <p className="ins-empty-small">{tr("No project data yet")}</p>
      </div>
    );
  }
  const topProjects = byProject.slice(0, 5);
  const max = topProjects[0]?.hours || 1;
  return (
    <div className="ins-project-breakdown">
      <div className="ins-project-breakdown__heading">
        <p className="ins-chart-title">{tr("Project focus")}</p>
        <span>{tr("This month")}</span>
      </div>
      <div className="ins-project-list">
        {topProjects.map((project) => (
          <div className="ins-project-row" key={project.project_name}>
            <div className="ins-project-row__meta">
              <span className="ins-project-row__name" title={project.project_name}>{project.project_name}</span>
              <strong>{fmtHours(project.hours)}</strong>
            </div>
            <div className="ins-project-row__track">
              <i style={{ width: `${Math.max(5, (project.hours / max) * 100)}%`, background: project.color || '#1a5c30' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Top trackers ───────────────────────────────────────────────────────────

function TopTrackers({ topTrackers }) {
  useTranslation();
  if (!topTrackers.length) return null;
  const max = topTrackers[0].hours || 1;
  return (
    <div className="ins-panel">
      <p className="ins-panel-title">{tr("Top trackers — this month")}</p>
      {topTrackers.map((t, i) => (
        <div className="ins-tracker-row" key={i}>
          <div className="ins-tracker-rank">#{i + 1}</div>
          <div className="ins-tracker-name">{t.name || tr("Unknown")}</div>
          <div className="ins-tracker-bar-wrap">
            <div className="ins-tracker-bar" style={{ width: `${(t.hours / max) * 100}%` }} />
          </div>
          <div className="ins-tracker-hours">{fmtHours(t.hours)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Team today ─────────────────────────────────────────────────────────────

function TeamToday({ teamToday }) {
  useTranslation();
  const { checked_in, checked_out, on_leave } = teamToday;
  const total = checked_in.length + checked_out.length + on_leave.length;
  if (total === 0) {
    return (
      <div className="ins-panel">
        <p className="ins-panel-title">{tr("Team today")}</p>
        <p className="ins-empty-small">{tr("No attendance records for today")}</p>
      </div>
    );
  }
  return (
    <div className="ins-panel">
      <p className="ins-panel-title">{tr("Team today")}</p>
      <div className="ins-today-grid">

        {checked_in.length > 0 && (
          <div className="ins-today-group">
            <div className="ins-today-group-label">
              <span className="ins-status-dot ins-status-dot--in" /> {tr("Checked in (")}{checked_in.length})
            </div>
            {checked_in.map((m, i) => (
              <div className="ins-today-row" key={i}>
                <div className="ins-today-avatar">{(m.name[0] || '?').toUpperCase()}</div>
                <div className="ins-today-info">
                  <div className="ins-today-name">{m.name}</div>
                  <div className="ins-today-meta">{tr("Since")} {m.check_in}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {checked_out.length > 0 && (
          <div className="ins-today-group">
            <div className="ins-today-group-label">
              <span className="ins-status-dot ins-status-dot--out" /> {tr("Done (")}{checked_out.length})
            </div>
            {checked_out.map((m, i) => (
              <div className="ins-today-row" key={i}>
                <div className="ins-today-avatar ins-today-avatar--out">{(m.name[0] || '?').toUpperCase()}</div>
                <div className="ins-today-info">
                  <div className="ins-today-name">{m.name}</div>
                  <div className="ins-today-meta">{m.check_in} – {m.check_out} · {fmtHours(m.worked_h)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {on_leave.length > 0 && (
          <div className="ins-today-group">
            <div className="ins-today-group-label">
              <span className="ins-status-dot ins-status-dot--leave" /> {tr("On leave (")}{on_leave.length})
            </div>
            {on_leave.map((m, i) => (
              <div className="ins-today-row" key={i}>
                <div className="ins-today-avatar ins-today-avatar--leave">{(m.name[0] || '?').toUpperCase()}</div>
                <div className="ins-today-info">
                  <div className="ins-today-name">{m.name}</div>
                  <div className="ins-today-meta">
                    <span className="ins-leave-type-pill" style={{ background: m.color + '22', color: m.color, border: `1px solid ${m.color}44` }}>
                      {m.leave_type}
                    </span>{tr("until")} {fmtUpcomingDate(m.until)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

// ── Upcoming leaves ────────────────────────────────────────────────────────

function UpcomingLeaves({ leaves }) {
  useTranslation();
  if (!leaves.length) return null;
  return (
    <div className="ins-panel">
      <p className="ins-panel-title">{tr("Upcoming leaves — next 14 days")}</p>
      <table className="ins-upcoming-table">
        <thead>
          <tr>
            <th>{tr("Employee")}</th>
            <th>{tr("Type")}</th>
            <th>{tr("From")}</th>
            <th>{tr("To")}</th>
            <th>{tr("Days")}</th>
          </tr>
        </thead>
        <tbody>
          {leaves.map((l, i) => (
            <tr key={i}>
              <td>{l.name}</td>
              <td>
                <span className="ins-leave-type-pill" style={{ background: l.color + '22', color: l.color, border: `1px solid ${l.color}44` }}>
                  {l.leave_type}
                </span>
              </td>
              <td>{fmtUpcomingDate(l.start_date)}</td>
              <td>{fmtUpcomingDate(l.end_date)}</td>
              <td>{l.days}{tr("d")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function InsightsPage() {
  useTranslation();
  const auth = useSelector((s) => s.auth);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!auth.workspace_id) return;
    setLoading(true);
    axios
      .get(`/api/report/insights?workspace_id=${auth.workspace_id}`, {
        headers: { Authorization: `Bearer ${auth.token}` },
      })
      .then((res) => { setData(res.data); setLoading(false); })
      .catch(() => { setError('Failed to load insights.'); setLoading(false); });
  }, [auth.workspace_id]);

  return (
    <div>
      <Sidebar />
      <div className="ins-page">
        <div className="ins-header">
          <span className="ins-header-eyebrow">{tr("Workspace analytics")}</span>
          <h1 className="ins-title">{tr("Insights")}</h1>
          <p className="ins-subtitle">{tr("A clear view of how time is being spent this month.")}</p>
        </div>

        {loading && <div className="ins-loading">{tr("Loading…")}</div>}
        {error   && <div className="ins-error">{error}</div>}

        {data && !loading && (() => {
          const delta = deltaLabel(data.hours_this_week, data.hours_last_week);
          return (
            <>
              {/* ── Stat cards ── */}
              <div className="ins-overview-grid">
              <div className="ins-cards">
                <StatCard label={tr("Hours this week")} value={fmtHours(data.hours_this_week)} delta={delta} accent="#1a5c30" />
                <StatCard label={tr("Avg daily (this month)")} value={fmtHours(data.avg_daily_hours)} accent="#4bc800" />
                <StatCard label={tr("Days tracked this month")} value={data.days_tracked} unit=" days" accent="#76a85f" />
                <StatCard label={tr("Billable % this month")} value={data.billable_pct} unit="%" accent="#2f7b48" />
              </div>
              <aside className="ins-attention-panel">
                <div className="ins-attention-panel__heading">
                  <span>{tr("Needs attention")}</span>
                  <small>{tr("Review before month end")}</small>
                </div>
                {data.overtime_month_h > 0 && (
                  <div className="ins-attention-item ins-attention-item--alert"><span>{tr("Overtime this month")}</span><strong>{fmtHours(data.overtime_month_h)}</strong></div>
                )}
                <div className="ins-attention-item"><span>{data.is_privileged ? tr("Pending leave requests") : tr("My pending requests")}</span><strong>{data.pending_leave}</strong></div>
                <LeaveCard leaveRemaining={data.leave_remaining} />
              </aside>
              </div>

              {/* ── Charts row 1: daily + project ── */}
              <div className="ins-charts ins-charts--2-1">
                <DailyBarChart daily7d={data.daily_7d} />
                <ProjectBreakdown byProject={data.by_project} />
              </div>

              {/* ── Chart row 2: day-of-week (full width) ── */}
              <div className="ins-charts ins-charts--full">
                <DowChart byDow={data.by_dow} />
              </div>

              {/* ── Team today + upcoming leaves (admin/manager) ── */}
              {data.is_privileged && (
                <div className="ins-two-col">
                  <TeamToday teamToday={data.team_today} />
                  <UpcomingLeaves leaves={data.upcoming_leaves} />
                </div>
              )}

              {/* ── Top trackers ── */}
              {data.is_privileged && <TopTrackers topTrackers={data.top_trackers} />}
            </>
          );
        })()}
      </div>
      <Footer />
    </div>
  );
}
